import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { gsap } from 'gsap';

/**
 * The CTS mascot duo — Hardy (hard-hat blob) and Cleo (clipboard inspector).
 *
 * Built in the Claude-mascot idiom: pure <rect> elements, no curves, no paths.
 * Cream pixel bodies, terracotta limbs (Claude's #DD775B palette tweaked for
 * CTS), dark eyes. Animated with GSAP using body tilts, eye shifts, and
 * svgOrigin pivots.
 *
 * Props
 *  - delay      seconds before intro plays
 *  - className  forwarded to the wrapper
 *
 * Imperative handle
 *  - celebrate()  short success bounce
 *  - sad()        droop / head-shake for error states
 */
// CTS ledger-industrial palette — readable on white form panel
const CREAM = '#E2E8F0';      // slate-200 body (visible on white)
const LIMB = '#334155';       // slate-700 limbs — strong silhouette
const ACCENT = '#8B2E52';     // brand pink — hat top, clip, pen
const SHADE = '#5C1E37';      // dark wine — hat brim, clip backing
const BAND = '#FFFFFF';       // white stripe accent on pink hat
const FACE = '#0F172A';       // slate-900 face features

const MascotDuo = forwardRef(function MascotDuo(
  { delay = 0, className = '' },
  externalRef,
) {
  const rootRef = useRef(null);

  // Outer wrappers — own the position-swap x translation independently of tilts/hops
  const hardyOuterRef = useRef(null);
  const cleoOuterRef = useRef(null);

  // Hardy
  const hardyGroupRef = useRef(null);
  const hardyBodyRef = useRef(null);
  const hardyHatGroupRef = useRef(null);
  const hardyHatTopRef = useRef(null);
  const hardyHatBrimRef = useRef(null);
  const hardyHatBandRef = useRef(null);
  const hardyHatCrestRef = useRef(null);
  const hardyEyesGroupRef = useRef(null);
  const hardyMouthRef = useRef(null);
  const hardyHandLRef = useRef(null);
  const hardyHandRRef = useRef(null);
  const hardyLegLRef = useRef(null);
  const hardyLegRRef = useRef(null);

  // Cleo
  const cleoGroupRef = useRef(null);
  const cleoBodyRef = useRef(null);
  const cleoHatGroupRef = useRef(null);
  const cleoHatTopRef = useRef(null);
  const cleoHatBrimRef = useRef(null);
  const cleoHatBandRef = useRef(null);
  const cleoHatCrestRef = useRef(null);
  const cleoLine1Ref = useRef(null);
  const cleoLine2Ref = useRef(null);
  const cleoLine3Ref = useRef(null);
  const cleoEyesGroupRef = useRef(null);
  const cleoMouthRef = useRef(null);
  const cleoHandLRef = useRef(null);
  const cleoHandRRef = useRef(null);
  const cleoPenRef = useRef(null);
  const cleoLegLRef = useRef(null);
  const cleoLegRRef = useRef(null);

  useImperativeHandle(externalRef, () => ({
    celebrate() {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) return;

      const hOuter = hardyOuterRef.current;
      const cOuter = cleoOuterRef.current;
      const hEyes = hardyEyesGroupRef.current;
      const cEyes = cleoEyesGroupRef.current;
      const hHat = hardyHatGroupRef.current;
      const cHat = cleoHatGroupRef.current;
      const hMouth = hardyMouthRef.current;
      const cMouth = cleoMouthRef.current;
      const hHand = hardyHandLRef.current;
      const cHand = cleoHandRRef.current;

      // Reset outer y in case the swap tween is mid-bob, then double-bounce jump.
      gsap.set([hOuter, cOuter], { y: 0 });
      const jump = gsap.timeline();
      jump
        .to([hOuter, cOuter], {
          y: -55,
          duration: 0.32,
          ease: 'power2.out',
          stagger: 0.07,
          overwrite: 'auto',
        })
        .to([hOuter, cOuter], {
          y: 0,
          duration: 0.26,
          ease: 'power2.in',
          stagger: 0.05,
        })
        .to([hOuter, cOuter], {
          y: -32,
          duration: 0.22,
          ease: 'power2.out',
          stagger: 0.04,
        })
        .to([hOuter, cOuter], {
          y: 0,
          duration: 0.4,
          ease: 'bounce.out',
          stagger: 0.04,
        });

      // Hats pop off the head briefly (look surprised + happy)
      gsap.fromTo(
        [hHat, cHat],
        { y: 0 },
        {
          y: -12,
          duration: 0.32,
          yoyo: true,
          repeat: 1,
          ease: 'power2.out',
          overwrite: 'auto',
        },
      );

      // Eyes go wide
      gsap.fromTo(
        [hEyes, cEyes],
        { scaleY: 1 },
        {
          scaleY: 1.55,
          duration: 0.3,
          yoyo: true,
          repeat: 1,
          ease: 'power2.out',
          overwrite: 'auto',
        },
      );

      // Mouth grin
      gsap.fromTo(
        [hMouth, cMouth],
        { scaleX: 1 },
        {
          scaleX: 1.6,
          transformOrigin: '50% 50%',
          duration: 0.4,
          yoyo: true,
          repeat: 1,
          ease: 'power2.out',
          overwrite: 'auto',
        },
      );

      // Both arms wave several times
      gsap.fromTo(
        hHand,
        { rotation: 0 },
        {
          rotation: -55,
          svgOrigin: '22 50',
          duration: 0.22,
          yoyo: true,
          repeat: 3,
          ease: 'power2.inOut',
          overwrite: 'auto',
        },
      );
      gsap.fromTo(
        cHand,
        { rotation: 0 },
        {
          rotation: 55,
          svgOrigin: '218 50',
          duration: 0.22,
          yoyo: true,
          repeat: 3,
          ease: 'power2.inOut',
          overwrite: 'auto',
        },
      );
    },
    sad() {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) return;
      gsap.to(hardyHatGroupRef.current, {
        rotation: -16,
        y: 4,
        svgOrigin: '54 26',
        duration: 0.4,
        ease: 'power2.out',
      });
      gsap.to(cleoGroupRef.current, {
        rotation: 4,
        svgOrigin: '184 114',
        duration: 0.18,
        yoyo: true,
        repeat: 5,
        ease: 'power1.inOut',
        onComplete() {
          gsap.to(cleoGroupRef.current, {
            rotation: 0,
            duration: 0.3,
            ease: 'power2.out',
          });
        },
      });
    },
  }));

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    // Snapshot refs
    const hardyOuter = hardyOuterRef.current;
    const cleoOuter = cleoOuterRef.current;
    const hardyGroup = hardyGroupRef.current;
    const hardyBody = hardyBodyRef.current;
    const hardyHatGroup = hardyHatGroupRef.current;
    const hardyHatTop = hardyHatTopRef.current;
    const hardyHatBrim = hardyHatBrimRef.current;
    const hardyHatBand = hardyHatBandRef.current;
    const hardyHatCrest = hardyHatCrestRef.current;
    const hardyEyesGroup = hardyEyesGroupRef.current;
    const hardyMouth = hardyMouthRef.current;
    const hardyHandL = hardyHandLRef.current;
    const hardyHandR = hardyHandRRef.current;
    const hardyLegL = hardyLegLRef.current;
    const hardyLegR = hardyLegRRef.current;

    const cleoGroup = cleoGroupRef.current;
    const cleoBody = cleoBodyRef.current;
    const cleoHatGroup = cleoHatGroupRef.current;
    const cleoHatTop = cleoHatTopRef.current;
    const cleoHatBrim = cleoHatBrimRef.current;
    const cleoHatBand = cleoHatBandRef.current;
    const cleoHatCrest = cleoHatCrestRef.current;
    const cleoLine1 = cleoLine1Ref.current;
    const cleoLine2 = cleoLine2Ref.current;
    const cleoLine3 = cleoLine3Ref.current;
    const cleoEyesGroup = cleoEyesGroupRef.current;
    const cleoMouth = cleoMouthRef.current;
    const cleoHandL = cleoHandLRef.current;
    const cleoHandR = cleoHandRRef.current;
    const cleoPen = cleoPenRef.current;
    const cleoLegL = cleoLegLRef.current;
    const cleoLegR = cleoLegRRef.current;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce) {
      gsap.set(root, { opacity: 1 });
      return undefined;
    }

    // Pre-intro: park both characters off-screen above, ready to drop in
    gsap.set(hardyGroup, { y: -160 });
    gsap.set(cleoGroup, { y: -160 });
    gsap.set([hardyEyesGroup, cleoEyesGroup], { scaleY: 1 });
    gsap.set([hardyHandL, hardyHandR, cleoHandL, cleoHandR], { rotation: 0 });
    gsap.set(root, { opacity: 1 });

    const intro = gsap.timeline({ delay });
    intro
      // Drop in — Hardy first, Cleo a beat behind
      .to(hardyGroup, {
        y: 0,
        duration: 0.7,
        ease: 'bounce.out',
      })
      .to(
        cleoGroup,
        {
          y: 0,
          duration: 0.75,
          ease: 'bounce.out',
        },
        '-=0.55',
      )
      // Big squash on landing (Hardy)
      .to(
        hardyBody,
        {
          scaleY: 0.75,
          scaleX: 1.18,
          svgOrigin: '54 86',
          duration: 0.12,
          ease: 'power2.out',
        },
        '-=0.35',
      )
      .to(hardyBody, {
        scaleY: 1,
        scaleX: 1,
        duration: 0.4,
        ease: 'elastic.out(1, 0.5)',
      })
      // Squash on Cleo landing
      .to(
        cleoBody,
        {
          scaleY: 0.78,
          scaleX: 1.15,
          svgOrigin: '184 86',
          duration: 0.12,
          ease: 'power2.out',
        },
        '-=0.5',
      )
      .to(cleoBody, {
        scaleY: 1,
        scaleX: 1,
        duration: 0.4,
        ease: 'elastic.out(1, 0.5)',
      }, '-=0.3')
      // Eyes go wide ("oh, we're here!")
      .fromTo(
        [hardyEyesGroup, cleoEyesGroup],
        { scaleY: 1.4 },
        { scaleY: 1, duration: 0.35, ease: 'back.out(2)' },
        '-=0.2',
      )
      // Hello beat — Hardy waves hand, Cleo wiggles pen, hats tip
      .to(
        hardyHandL,
        {
          rotation: -35,
          svgOrigin: '22 50',
          duration: 0.32,
          yoyo: true,
          repeat: 1,
          ease: 'power2.inOut',
        },
        '+=0.15',
      )
      .to(
        cleoPen,
        {
          rotation: 22,
          svgOrigin: '222 36',
          duration: 0.32,
          yoyo: true,
          repeat: 1,
          ease: 'power2.inOut',
        },
        '<',
      )
      .to(
        hardyHatGroup,
        {
          rotation: 8,
          svgOrigin: '54 26',
          duration: 0.32,
          yoyo: true,
          repeat: 1,
          ease: 'power2.inOut',
        },
        '<',
      )
      .to(
        cleoHatGroup,
        {
          rotation: -8,
          svgOrigin: '184 26',
          duration: 0.32,
          yoyo: true,
          repeat: 1,
          ease: 'power2.inOut',
        },
        '<',
      );

    // Idle loops
    const idleTweens = [];
    const startIdle = () => {
      // Body tilt — Claude lean
      idleTweens.push(
        gsap.fromTo(
          hardyGroup,
          { rotation: -2 },
          {
            rotation: 2,
            svgOrigin: '54 114',
            duration: 2.6,
            yoyo: true,
            repeat: -1,
            ease: 'sine.inOut',
          },
        ),
        gsap.fromTo(
          cleoGroup,
          { rotation: 2 },
          {
            rotation: -2,
            svgOrigin: '184 114',
            duration: 2.8,
            yoyo: true,
            repeat: -1,
            ease: 'sine.inOut',
          },
        ),
      );

      // Walk-in-place — legs alternate up/down
      idleTweens.push(
        gsap.fromTo(
          hardyLegL,
          { y: 0 },
          { y: -4, duration: 0.42, yoyo: true, repeat: -1, ease: 'sine.inOut' },
        ),
        gsap.fromTo(
          hardyLegR,
          { y: -4 },
          { y: 0, duration: 0.42, yoyo: true, repeat: -1, ease: 'sine.inOut' },
        ),
        gsap.fromTo(
          cleoLegL,
          { y: -4 },
          { y: 0, duration: 0.46, yoyo: true, repeat: -1, ease: 'sine.inOut' },
        ),
        gsap.fromTo(
          cleoLegR,
          { y: 0 },
          { y: -4, duration: 0.46, yoyo: true, repeat: -1, ease: 'sine.inOut' },
        ),
      );

      // Hand sway — gentle counter to body
      idleTweens.push(
        gsap.fromTo(
          hardyHandL,
          { rotation: -3 },
          {
            rotation: 3,
            svgOrigin: '22 50',
            duration: 2.6,
            yoyo: true,
            repeat: -1,
            ease: 'sine.inOut',
          },
        ),
        gsap.fromTo(
          hardyHandR,
          { rotation: 3 },
          {
            rotation: -3,
            svgOrigin: '86 50',
            duration: 2.6,
            yoyo: true,
            repeat: -1,
            ease: 'sine.inOut',
          },
        ),
        gsap.fromTo(
          cleoHandL,
          { rotation: 3 },
          {
            rotation: -3,
            svgOrigin: '154 50',
            duration: 2.8,
            yoyo: true,
            repeat: -1,
            ease: 'sine.inOut',
          },
        ),
        gsap.fromTo(
          cleoHandR,
          { rotation: -3 },
          {
            rotation: 3,
            svgOrigin: '218 50',
            duration: 2.8,
            yoyo: true,
            repeat: -1,
            ease: 'sine.inOut',
          },
        ),
      );

      // Periodic gestures
      idleTweens.push(
        // Hardy hop every 11s
        gsap.fromTo(
          hardyGroup,
          { y: 0 },
          {
            y: -12,
            duration: 0.28,
            yoyo: true,
            repeat: -1,
            repeatDelay: 11,
            ease: 'power3.out',
          },
        ),
        // Hardy wave every 8s
        gsap.fromTo(
          hardyHandL,
          { rotation: 0 },
          {
            rotation: -30,
            svgOrigin: '22 50',
            duration: 0.4,
            yoyo: true,
            repeat: -1,
            repeatDelay: 8,
            ease: 'power2.inOut',
            delay: 3,
          },
        ),
        // Cleo pen scribble every 6s
        gsap.fromTo(
          cleoPen,
          { rotation: 0, y: 0 },
          {
            rotation: 12,
            y: 5,
            svgOrigin: '222 36',
            duration: 0.22,
            yoyo: true,
            repeat: -1,
            repeatDelay: 6,
            ease: 'power2.inOut',
          },
        ),
        // Cleo hop every 13s (offset from Hardy)
        gsap.fromTo(
          cleoGroup,
          { y: 0 },
          {
            y: -12,
            duration: 0.28,
            yoyo: true,
            repeat: -1,
            repeatDelay: 13,
            ease: 'power3.out',
            delay: 5,
          },
        ),
        // Hardy hat tip every 10s
        gsap.fromTo(
          hardyHatGroup,
          { rotation: 0 },
          {
            rotation: 5,
            svgOrigin: '54 26',
            duration: 0.4,
            yoyo: true,
            repeat: -1,
            repeatDelay: 10,
            ease: 'power2.inOut',
            delay: 4,
          },
        ),
        // Cleo hat tip every 9s (offset from Hardy)
        gsap.fromTo(
          cleoHatGroup,
          { rotation: 0 },
          {
            rotation: -5,
            svgOrigin: '184 26',
            duration: 0.4,
            yoyo: true,
            repeat: -1,
            repeatDelay: 9,
            ease: 'power2.inOut',
            delay: 7,
          },
        ),
      );

      // Position swap — characters walk across and switch places, then back
      const SWAP_DISTANCE = 130; // viewBox units between Hardy and Cleo's anchor centers
      const SWAP_DURATION = 2.4;
      const SWAP_HOLD = 7;
      const swapTl = gsap.timeline({ repeat: -1, delay: 4 });
      swapTl
        // Walk right: Hardy → Cleo's spot, Cleo → Hardy's spot
        .to(hardyOuter, { x: SWAP_DISTANCE, duration: SWAP_DURATION, ease: 'power1.inOut' })
        .to(
          cleoOuter,
          { x: -SWAP_DISTANCE, duration: SWAP_DURATION, ease: 'power1.inOut' },
          '<',
        )
        // Walk-bob (subtle arc up + down during crossing)
        .to(
          hardyOuter,
          { y: -6, duration: SWAP_DURATION / 2, yoyo: true, repeat: 1, ease: 'sine.inOut' },
          '<',
        )
        .to(
          cleoOuter,
          { y: -6, duration: SWAP_DURATION / 2, yoyo: true, repeat: 1, ease: 'sine.inOut' },
          '<',
        )
        // Settle at swapped positions
        .to({}, { duration: SWAP_HOLD })
        // Walk back to original
        .to(hardyOuter, { x: 0, duration: SWAP_DURATION, ease: 'power1.inOut' })
        .to(
          cleoOuter,
          { x: 0, duration: SWAP_DURATION, ease: 'power1.inOut' },
          '<',
        )
        .to(
          hardyOuter,
          { y: -6, duration: SWAP_DURATION / 2, yoyo: true, repeat: 1, ease: 'sine.inOut' },
          '<',
        )
        .to(
          cleoOuter,
          { y: -6, duration: SWAP_DURATION / 2, yoyo: true, repeat: 1, ease: 'sine.inOut' },
          '<',
        )
        .to({}, { duration: SWAP_HOLD });
      idleTweens.push(swapTl);

      // Blinks
      const blink = (node, minDelay, maxDelay) => {
        if (!node) return;
        const tween = gsap.to(node, {
          scaleY: 0.05,
          transformOrigin: '50% 50%',
          duration: 0.09,
          ease: 'power2.in',
          yoyo: true,
          repeat: 1,
          onComplete() {
            const wait = minDelay + Math.random() * (maxDelay - minDelay);
            const call = gsap.delayedCall(wait, () =>
              blink(node, minDelay, maxDelay),
            );
            idleTweens.push(call);
          },
        });
        idleTweens.push(tween);
      };
      blink(hardyEyesGroup, 4, 7);
      blink(cleoEyesGroup, 5, 8);
    };
    intro.eventCallback('onComplete', startIdle);

    // Pointer-aware eye tracking
    const supportsHover = window.matchMedia?.('(hover: hover)').matches;
    const hoverHost = root.parentElement;
    const onMove = (e) => {
      const rect = root.getBoundingClientRect();
      const dx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      const dy = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      gsap.to([hardyEyesGroup, cleoEyesGroup], {
        x: dx * 2.5,
        y: dy * 2,
        duration: 0.4,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    };
    const onLeave = () => {
      gsap.to([hardyEyesGroup, cleoEyesGroup], {
        x: 0,
        y: 0,
        duration: 0.5,
        ease: 'power3.out',
        overwrite: 'auto',
      });
    };
    if (supportsHover && hoverHost) {
      hoverHost.addEventListener('mousemove', onMove);
      hoverHost.addEventListener('mouseleave', onLeave);
    }

    return () => {
      intro.kill();
      idleTweens.forEach((t) => t.kill?.());
      gsap.killTweensOf([
        hardyOuter, cleoOuter,
        hardyGroup, hardyBody, hardyHatGroup, hardyHatTop, hardyHatBrim, hardyHatBand, hardyHatCrest,
        hardyEyesGroup, hardyMouth, hardyHandL, hardyHandR, hardyLegL, hardyLegR,
        cleoGroup, cleoBody, cleoHatGroup, cleoHatTop, cleoHatBrim, cleoHatBand, cleoHatCrest,
        cleoLine1, cleoLine2, cleoLine3,
        cleoEyesGroup, cleoMouth, cleoHandL, cleoHandR, cleoPen, cleoLegL, cleoLegR,
      ]);
      if (supportsHover && hoverHost) {
        hoverHost.removeEventListener('mousemove', onMove);
        hoverHost.removeEventListener('mouseleave', onLeave);
      }
    };
  }, [delay]);

  return (
    <div
      ref={rootRef}
      className={`relative ${className}`}
      role="img"
      aria-label="Civil Tech Veritas mascots"
      style={{ opacity: 0 }}
    >
      <svg
        viewBox="0 0 240 120"
        width="100%"
        height="100%"
        shapeRendering="crispEdges"
        preserveAspectRatio="xMidYMax meet"
        aria-hidden
      >
        {/* ── Hardy ────────────────────────────────────────────── */}
        <g ref={hardyOuterRef}>
        <g ref={hardyGroupRef}>
          {/* Legs */}
          <rect ref={hardyLegLRef} x="38" y="88" width="10" height="26" fill={LIMB} />
          <rect ref={hardyLegRRef} x="60" y="88" width="10" height="26" fill={LIMB} />
          {/* Body */}
          <rect ref={hardyBodyRef} x="30" y="32" width="48" height="58" fill={CREAM} />
          {/* Hands */}
          <rect ref={hardyHandLRef} x="18" y="46" width="10" height="24" fill={LIMB} />
          <rect ref={hardyHandRRef} x="80" y="46" width="10" height="24" fill={LIMB} />
          {/* Construction hardhat */}
          <g ref={hardyHatGroupRef}>
            {/* Brim (widest, sits on head) */}
            <rect ref={hardyHatBrimRef} x="16" y="28" width="76" height="4" fill={SHADE} />
            {/* Dome */}
            <rect ref={hardyHatTopRef} x="32" y="14" width="44" height="14" fill={ACCENT} />
            {/* Side flanges (stepped widening near the brim — classic hardhat profile) */}
            <rect x="28" y="22" width="4" height="6" fill={ACCENT} />
            <rect x="76" y="22" width="4" height="6" fill={ACCENT} />
            {/* Reflective band stripe */}
            <rect ref={hardyHatBandRef} x="32" y="24" width="44" height="3" fill={BAND} />
            {/* Crest ridge on top */}
            <rect ref={hardyHatCrestRef} x="48" y="10" width="12" height="4" fill={ACCENT} />
          </g>
          {/* Eyes */}
          <g ref={hardyEyesGroupRef}>
            <rect x="42" y="44" width="5" height="9" fill={FACE} />
            <rect x="61" y="44" width="5" height="9" fill={FACE} />
          </g>
          {/* Mouth */}
          <rect ref={hardyMouthRef} x="48" y="62" width="12" height="3" fill={FACE} />
        </g>
        </g>

        {/* ── Cleo ─────────────────────────────────────────────── */}
        <g ref={cleoOuterRef}>
        <g ref={cleoGroupRef}>
          {/* Legs */}
          <rect ref={cleoLegLRef} x="166" y="88" width="10" height="26" fill={LIMB} />
          <rect ref={cleoLegRRef} x="192" y="88" width="10" height="26" fill={LIMB} />
          {/* Body */}
          <rect ref={cleoBodyRef} x="156" y="30" width="58" height="60" fill={CREAM} />
          {/* Hands */}
          <rect ref={cleoHandLRef} x="148" y="46" width="10" height="24" fill={LIMB} />
          <rect ref={cleoHandRRef} x="214" y="46" width="10" height="24" fill={LIMB} />
          {/* Construction hardhat — matches Hardy's */}
          <g ref={cleoHatGroupRef}>
            {/* Brim */}
            <rect ref={cleoHatBrimRef} x="146" y="26" width="76" height="4" fill={SHADE} />
            {/* Dome */}
            <rect ref={cleoHatTopRef} x="162" y="12" width="44" height="14" fill={ACCENT} />
            {/* Side flanges */}
            <rect x="158" y="20" width="4" height="6" fill={ACCENT} />
            <rect x="206" y="20" width="4" height="6" fill={ACCENT} />
            {/* Reflective band */}
            <rect ref={cleoHatBandRef} x="162" y="22" width="44" height="3" fill={BAND} />
            {/* Crest ridge */}
            <rect ref={cleoHatCrestRef} x="178" y="8" width="12" height="4" fill={ACCENT} />
          </g>
          {/* Ruled page lines */}
          <rect ref={cleoLine1Ref} x="164" y="68" width="42" height="2" fill={LIMB} opacity="0.4" />
          <rect ref={cleoLine2Ref} x="164" y="74" width="34" height="2" fill={LIMB} opacity="0.4" />
          <rect ref={cleoLine3Ref} x="164" y="80" width="38" height="2" fill={LIMB} opacity="0.4" />
          {/* Eyes */}
          <g ref={cleoEyesGroupRef}>
            <rect x="170" y="44" width="5" height="9" fill={FACE} />
            <rect x="195" y="44" width="5" height="9" fill={FACE} />
          </g>
          {/* Mouth */}
          <rect ref={cleoMouthRef} x="178" y="62" width="14" height="3" fill={FACE} />
          {/* Pen — held by right hand */}
          <rect ref={cleoPenRef} x="220" y="32" width="4" height="20" fill={ACCENT} />
        </g>
        </g>
      </svg>
    </div>
  );
});

export default MascotDuo;
